import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import OfferCard from "@/components/base/OfferCard";
import { useAdminDeals, useAdminProducts } from "@/hooks/useAdminStore";
import { useArticleCatalog } from "@/hooks/useArticleCatalog";
import { formatCanonicalDateForDisplay } from "@/utils/date";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { usePageSeo } from "@/hooks/usePageSeo";

export default function SearchPage() {
  const { products } = useAdminProducts();
  const { deals } = useAdminDeals();
  const { articles } = useArticleCatalog();
  const { settings } = useSiteSettings();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";

  const matchedProducts = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    const catalog = Array.from(new Map([...products, ...deals].map((product) => [product.slug, product])).values());

    return catalog.filter(
      (product) =>
        product.name.toLowerCase().includes(q) ||
        product.category.toLowerCase().includes(q) ||
        product.marketplace.toLowerCase().includes(q)
    );
  }, [deals, products, query]);

  const matchedArticles = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    return articles.filter(
      (guide) =>
        guide.title.toLowerCase().includes(q) ||
        guide.excerpt.toLowerCase().includes(q) ||
        guide.category.toLowerCase().includes(q)
    );
  }, [articles, query]);

  const totalResults = matchedProducts.length + matchedArticles.length;

  usePageSeo({
    title: query ? `Busca por "${query}" | ${settings.siteName}` : `Busca | ${settings.siteName}`,
    description: query
      ? `${totalResults} resultado${totalResults !== 1 ? "s" : ""} para ${query} em produtos, ofertas e artigos do ${settings.siteName}.`
      : `Pesquise produtos, ofertas e artigos no ${settings.siteName}.`,
    canonicalPath: "/busca",
    keywords: settings.seoKeywords,
    robots: "noindex,follow",
    image: settings.logoUrl,
    siteName: settings.siteName,
    ogType: "website",
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-orange-500 transition-colors">
              Home
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-600">Busca</span>
          </nav>
          <h1 className="text-2xl md:text-3xl font-black text-gray-900 mb-1">
            {query ? (
              <>
                Resultados para <span className="text-orange-500">"{query}"</span>
              </>
            ) : (
              "Busca"
            )}
          </h1>
          {query && (
            <p className="text-gray-500 text-sm">
              {totalResults === 0
                ? "Nenhum resultado encontrado"
                : `${totalResults} resultado${totalResults !== 1 ? "s" : ""} encontrado${totalResults !== 1 ? "s" : ""}`}
            </p>
          )}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        {!query && (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
              <i className="ri-search-line text-gray-300 text-2xl"></i>
            </div>
            <p className="text-gray-500 font-semibold">Digite algo para buscar</p>
            <p className="text-gray-400 text-sm mt-1">Pesquise por produtos, marketplaces, categorias ou artigos</p>
          </div>
        )}

        {query && totalResults === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
              <i className="ri-search-line text-gray-300 text-2xl"></i>
            </div>
            <p className="text-gray-500 font-semibold">Nenhum resultado para "{query}"</p>
            <p className="text-gray-400 text-sm mt-1">
              Tente outros termos ou navegue pelas <Link to="/categorias" className="text-orange-500 hover:underline">categorias</Link>
            </p>
          </div>
        )}

        {matchedProducts.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-black text-gray-900">
                Produtos <span className="text-gray-400 font-normal text-base">({matchedProducts.length})</span>
              </h2>
              <Link
                to="/categorias"
                className="text-sm text-orange-500 hover:text-orange-600 font-semibold transition-colors"
              >
                Ver todos
                <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {matchedProducts.map((product) => (
                <OfferCard
                  key={product.slug}
                  item={{
                    slug: product.slug,
                    image: product.image,
                    name: product.name,
                    price: product.price,
                    originalPrice: product.originalPrice,
                    discount: product.discount,
                    marketplace: product.marketplace,
                    freeShipping: product.freeShipping,
                    fulfillment: "fulfillment" in product ? product.fulfillment : false,
                    officialStoreName:
                      "officialStoreName" in product
                        ? product.officialStoreName || ("officialStore" in product && product.officialStore ? "Loja Oficial" : "")
                        : "",
                    affiliateLink: product.affiliateLink,
                    isFlash: product.badge === "Oferta Relâmpago",
                    category: product.category,
                    categorySlug: product.categorySlug,
                    subcategory: "subcategory" in product ? product.subcategory : product.category,
                    subcategorySlug: "subcategorySlug" in product ? product.subcategorySlug : product.categorySlug,
                  }}
                />
              ))}
            </div>
          </section>
        )}

        {matchedArticles.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-black text-gray-900">
                Artigos & Guias <span className="text-gray-400 font-normal text-base">({matchedArticles.length})</span>
              </h2>
              <Link
                to="/guias"
                className="text-sm text-orange-500 hover:text-orange-600 font-semibold transition-colors"
              >
                Ver todos
                <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {matchedArticles.map((guide) => (
                <Link
                  key={guide.id}
                  to={`/artigo/${guide.slug}`}
                  className="group bg-white rounded-2xl border border-gray-100 hover:border-orange-200 overflow-hidden transition-all hover:-translate-y-0.5 cursor-pointer"
                >
                  <div className="w-full h-44 overflow-hidden">
                    <img
                      src={guide.image}
                      alt={guide.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-orange-500 text-xs font-bold">{guide.category}</span>
                    <h3 className="font-bold text-gray-800 text-sm leading-snug mt-1 mb-2 line-clamp-2 group-hover:text-orange-500 transition-colors">
                      {guide.title}
                    </h3>
                    <p className="text-gray-500 text-xs line-clamp-2 mb-3">{guide.excerpt}</p>
                    <div className="flex items-center justify-between text-gray-400 text-xs">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {guide.readTime}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {guide.views.toLocaleString("pt-BR")}
                        </span>
                      </div>
                      <span>{formatCanonicalDateForDisplay(guide.date)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}